/* global mainScript */
import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { motion } from "framer-motion";

import Slide from "./Slide";

import { Sizes } from "../../styles";

const LargeCarousel = ({ slidesJson }) => {
	const [slides] = useState(JSON.parse(slidesJson));
	const [length] = useState(slides.length);
	const [currentIndex, setCurrentIndex] = useState(0);

	// used for animating the slides on toggle
	const ref = useRef(0);

	const toggleNexSlide = () => {
		if (currentIndex < length - 1) {
			ref.current++; // used for triggering animation
			return setCurrentIndex((prevState) => prevState + 1);
		}
	};

	const togglePrevSlide = () => {
		if (currentIndex > 0) {
			ref.current++; // used for triggering animation
			return setCurrentIndex((prevState) => prevState - 1);
		}
	};

	if (!slides) return null;

	return (
		<Carousel>
			<PrevArrow
				className="carousel-prev-arrow"
				aria-label="slider previous arrow"
				isDisabled={currentIndex + 1 === 1}
				onKeyPress={togglePrevSlide}
				onMouseDown={(e) => {
					e.preventDefault();
					togglePrevSlide();
				}}
			/>
			<SlideContainer
				animate={ref.current === 0 ? {} : { opacity: [0, 1] }}
				transition={{
					duration: 0.7,
				}}
				key={ref.current}
			>
				<Slide
					togglePrevSlide={togglePrevSlide}
					toggleNexSlide={toggleNexSlide}
					slide={slides[currentIndex]}
					currentIndex={currentIndex}
					length={length}
				/>
			</SlideContainer>
			<NextArrow
				className="carousel-next-arrow"
				aria-label="slider next arrow"
				isDisabled={currentIndex + 1 > length - 1}
				onKeyPress={toggleNexSlide}
				onMouseDown={(e) => {
					e.preventDefault();
					toggleNexSlide();
				}}
			/>
		</Carousel>
	);
};

LargeCarousel.propTypes = {
	slidesJson: PropTypes.string,
};

const Carousel = styled(motion.div)`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 2.3rem;
	min-height: 431px;

	@media only screen and (min-width: ${Sizes.screens.medium}) {
		gap: 4.3rem;
		min-height: unset;
	}
`;

const SlideContainer = styled(motion.div)`

	@media only screen and (min-width: ${Sizes.screens.medium}) {
		flex: 1;
		max-width: 966px;
	}
`;

/* 
the important flags are added here purely for the editor experience
WordPress forces button styles to show up on the editor side
as of now !important is the only thing that stops that from happening 
*/
const Arrow = styled.button`
	background: none !important;
	padding: 0;
	height: 3rem;
	width: 5rem;
	display: flex;
	align-items: center;
	position: relative;
	top: -90px;

	opacity: ${(props) => (props.isDisabled ? "0.5" : "1")};
	cursor: ${(props) => (props.isDisabled ? "default" : "pointer")};

	@media only screen and (min-width: ${Sizes.screens.medium}) {
		top: -90px;
	}

	&:hover,
	&:focus {
		background: none !important;
		box-shadow: none !important;
	}

	&:focus {
		border: 1px solid blue;
	}
`;

const PrevArrow = styled(Arrow)`
	justify-content: flex-end;
	&::after {
		content: url(${mainScript.assetPath +
		"images/svg/icons/icon-chevron-left.svg"});
		transition: all 0.2s ease-in;
		margin-top: 4px;
	}
`;

const NextArrow = styled(Arrow)`
	justify-content: flex-start;

	&::after {
		content: url(${mainScript.assetPath +
		"images/svg/icons/icon-chevron-right.svg"});
		transition: all 0.2s ease-in;
		margin-top: 4px;
	}
`;

export default LargeCarousel;
